import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { defaultMetaObj, initialLeadActivityState } from "store/constants";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const {
  leadActivityEndPoint,
  tokenActivityEndPoint,
  propertyActivityEndpoint,
  staffTargetActivityEndPoint,
  individualPropertyActivityEndPoint,
  taskActivityEndPoint,
  teamActivityEndPoint,
} = endPoints;

export const fetchAllLeadActivities = createAsyncThunk(
  "leadActivities/fetchAllLeadActivities",
  async (
    { page, leadId }: { page: number; leadId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: leadActivityEndPoint,
      searchParams: {
        lead__lead_id: leadId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTokenActivities = createAsyncThunk(
  "leadActivities/fetchAllTokenActivities",
  async (
    { page, tokenId }: { page: number; tokenId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: tokenActivityEndPoint,
      searchParams: {
        token__token_id: tokenId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllPropertyActivities = createAsyncThunk(
  "leadActivities/fetchAllPropertyActivities",
  async (
    { page, propertyId }: { page: number; propertyId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: propertyActivityEndpoint,
      searchParams: {
        property_unit__id: propertyId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllStaffTargetActivities = createAsyncThunk(
  "leadActivities/fetchAllStaffTargetActivities",
  async (
    { page, targetId }: { page: number; targetId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: staffTargetActivityEndPoint,
      searchParams: {
        task_target__id: targetId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchindividualPropertyActivities = createAsyncThunk(
  "leadActivities/fetchindividualPropertyActivities",
  async (
    { page, propertyId }: { page: number; propertyId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: individualPropertyActivityEndPoint,
      searchParams: {
        individual_property__id: propertyId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTaskActivities = createAsyncThunk(
  "leadActivities/fetchAllTaskActivities",
  async (
    { page, taskId }: { page: number; taskId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: taskActivityEndPoint,
      searchParams: {
        task__task_id: taskId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTeamActivities = createAsyncThunk(
  "leadActivities/fetchAllTeamActivities",
  async (
    { page, teamId }: { page: number; teamId: string },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: teamActivityEndPoint,
      searchParams: {
        team__id: teamId,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const leadActivitySlice = createSlice({
  name: "leadActivities",
  initialState: initialLeadActivityState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all lead activities
    builder
      .addCase(fetchAllLeadActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllLeadActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.leadActivities = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllLeadActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.leadActivities = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all token activities
    builder
      .addCase(fetchAllTokenActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTokenActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.tokenActivities = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTokenActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.tokenActivities = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all property activities
    builder
      .addCase(fetchAllPropertyActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllPropertyActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.propertyActivities = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllPropertyActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.propertyActivities = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all staff target activities
    builder
      .addCase(fetchAllStaffTargetActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllStaffTargetActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.staffTarget = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllStaffTargetActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.staffTarget = [];
          state.meta = defaultMetaObj;
        }
      );
    // fetch all individual property activities
    builder
      .addCase(fetchindividualPropertyActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchindividualPropertyActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.individualPropertyActivities = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchindividualPropertyActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.individualPropertyActivities = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all task activities
    builder
      .addCase(fetchAllTaskActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTaskActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.taskActivities = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTaskActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.taskActivities = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all team activities
    builder
      .addCase(fetchAllTeamActivities.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTeamActivities.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.teamActivities = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTeamActivities.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.teamActivities = [];
          state.meta = defaultMetaObj;
        }
      );
  },
});

export const leadActivityReducer = leadActivitySlice.reducer;
